import { useState, useRef, useEffect } from 'react'
import { IntlShape } from 'react-intl'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { Modal } from 'react-bootstrap'
import { ID } from '../../../../../../../_dashboard/helpers'
import { getAll, createOne, patchOne, deleteOne } from '../../../../../../services/common_requests'
import PromotionsForm, { TourPromotionItem } from './PromotionsForm'
import { useConfirm } from '../../../../../../components/ConfirmProvider'

declare const KTMenu: any

interface PromotionsProps {
  tourId?: string
  intl: IntlShape
  showAllTours?: boolean
}

const Promotions: React.FC<PromotionsProps> = ({ tourId = '', intl, showAllTours = false }) => {
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [currentItem, setCurrentItem] = useState<TourPromotionItem | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [showDropdownId, setShowDropdownId] = useState<string | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const formRef = useRef<{ handleSubmit: () => void }>(null)
  const confirm = useConfirm()

  const listUrl = tourId && !showAllTours
    ? `/tour-promotions?tourId=${tourId}&size=100`
    : '/tour-promotions?size=100'

  const { data: promotionsData, isLoading } = useQuery(
    ['tourPromotions', tourId, showAllTours],
    () => getAll(null, listUrl),
    {
      retry: 1,
      onError: () => toast.error(intl.formatMessage({ id: 'TOUR.PROMOTIONS.FETCH_ERROR' })),
    },
  )

  const invalidate = () => queryClient.invalidateQueries(['tourPromotions'])

  const createMutation = useMutation(
    (data: Omit<TourPromotionItem, '_id'>) => createOne(data, '/tour-promotions'),
    {
      onSuccess: () => {
        invalidate()
        toast.success(intl.formatMessage({ id: 'TOUR.PROMOTIONS.CREATE_SUCCESS' }))
        closeModal()
        setIsSaving(false)
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'TOUR.PROMOTIONS.CREATE_ERROR' }))
        setIsSaving(false)
      },
    },
  )

  const updateMutation = useMutation(
    ({ id, data }: { id: string; data: Partial<TourPromotionItem> }) =>
      patchOne(data, `/tour-promotions/${id}`),
    {
      onSuccess: () => {
        invalidate()
        toast.success(intl.formatMessage({ id: 'TOUR.PROMOTIONS.UPDATE_SUCCESS' }))
        closeModal()
        setIsSaving(false)
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'TOUR.PROMOTIONS.UPDATE_ERROR' }))
        setIsSaving(false)
      },
    },
  )

  const deleteMutation = useMutation(
    (id: ID) => deleteOne(id, '/tour-promotions'),
    {
      onSuccess: () => {
        invalidate()
        toast.success(intl.formatMessage({ id: 'TOUR.PROMOTIONS.DELETE_SUCCESS' }))
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'TOUR.PROMOTIONS.DELETE_ERROR' }))
      },
    },
  )

  const resetUsageMutation = useMutation(
    (id: string) => patchOne({}, `/tour-promotions/${id}/reset-usage`),
    {
      onSuccess: () => {
        invalidate()
        toast.success(intl.formatMessage({ id: 'TOUR.PROMOTIONS.RESET_SUCCESS' }))
      },
      onError: () => {
        toast.error(intl.formatMessage({ id: 'TOUR.PROMOTIONS.RESET_ERROR' }))
      },
    },
  )

  const handleSubmit = (values: TourPromotionItem) => {
    if (
      values.appliesTo === 'specific_tours' &&
      showAllTours &&
      !(values.tourIds?.length)
    ) {
      toast.error(intl.formatMessage({ id: 'TOUR.PROMOTIONS.TOUR_REQUIRED' }))
      setIsSaving(false)
      return
    }

    setIsSaving(true)
    const payload: Record<string, unknown> = {
      name: values.name.trim(),
      description: values.description?.trim() || undefined,
      code: values.code.trim().toUpperCase(),
      promotionType: values.promotionType,
      value: Number(values.value),
      appliesTo: values.appliesTo,
      tourIds:
        values.appliesTo === 'specific_tours'
          ? (values.tourIds?.length ? values.tourIds : tourId ? [tourId] : [])
          : [],
      maxUsage: values.maxUsage != null ? Number(values.maxUsage) : undefined,
      minSubtotalTTC:
        values.minSubtotalTTC != null ? Number(values.minSubtotalTTC) : undefined,
      maxDiscountTTC:
        values.maxDiscountTTC != null ? Number(values.maxDiscountTTC) : undefined,
      minParticipants:
        values.minParticipants != null ? Number(values.minParticipants) : undefined,
      stackableWithGroupDiscount: values.stackableWithGroupDiscount !== false,
      currency: values.currency || 'EUR',
      isActive: values.isActive,
      startDate: values.startDate ? new Date(values.startDate).toISOString() : undefined,
      endDate: values.endDate ? new Date(values.endDate).toISOString() : undefined,
    }

    if (currentItem?._id) {
      updateMutation.mutate({ id: currentItem._id, data: payload })
    } else {
      createMutation.mutate(payload as Omit<TourPromotionItem, '_id'>)
    }
  }

  const handleAddNew = () => {
    setCurrentItem({
      name: '',
      code: '',
      promotionType: 'percentage',
      value: 10,
      appliesTo: tourId ? 'specific_tours' : 'all_tours',
      tourIds: tourId ? [tourId] : [],
      stackableWithGroupDiscount: true,
      isActive: true,
      currency: 'EUR',
    })
    setModalOpen(true)
  }

  const closeModal = () => {
    setModalOpen(false)
    setCurrentItem(null)
  }

  const formatDate = (dateString?: string) =>
    dateString ? new Date(dateString).toLocaleDateString() : '—'

  const formatValue = (item: TourPromotionItem) =>
    item.promotionType === 'percentage' ? `${item.value}%` : `${item.value} ${item.currency || 'EUR'}`

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdownId(null)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const items: TourPromotionItem[] = promotionsData?.data || []

  if (isLoading) {
    return <div className='text-center py-10'>{intl.formatMessage({ id: 'COMMON.LOADING' })}</div>
  }

  return (
    <>
      <div className='d-flex justify-content-end mb-6'>
        <button type='button' className='btn btn-primary' onClick={handleAddNew}>
          <i className='ki-solid ki-plus fs-2'></i>
          {intl.formatMessage({ id: 'TOUR.PROMOTIONS.ADD' })}
        </button>
      </div>

      {items.length === 0 ? (
        <div className='text-center py-10'>
          <h3 className='fs-4 fw-bold mb-5'>
            {intl.formatMessage({ id: 'TOUR.PROMOTIONS.NO_ITEMS' })}
          </h3>
        </div>
      ) : (
        <div className='table-responsive'>
          <table className='table align-middle table-row-dashed fs-6 gy-5'>
            <thead>
              <tr className='text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0'>
                <th>{intl.formatMessage({ id: 'TOUR.PROMOTIONS.CODE' })}</th>
                <th>{intl.formatMessage({ id: 'TOUR.PROMOTIONS.NAME' })}</th>
                <th>{intl.formatMessage({ id: 'TOUR.PROMOTIONS.VALUE' })}</th>
                <th>{intl.formatMessage({ id: 'TOUR.PROMOTIONS.USAGE' })}</th>
                <th>{intl.formatMessage({ id: 'TOUR.PROMOTIONS.DATE_RANGE' })}</th>
                <th>{intl.formatMessage({ id: 'TOUR.PROMOTIONS.ACTIVE' })}</th>
                <th className='text-end'>{intl.formatMessage({ id: 'COMMON.ACTIONS' })}</th>
              </tr>
            </thead>
            <tbody className='fw-semibold text-gray-600'>
              {items.map((item) => (
                <tr key={item._id}>
                  <td>
                    <span className='badge badge-light-primary'>{item.code}</span>
                  </td>
                  <td>{item.name}</td>
                  <td>{formatValue(item)}</td>
                  <td>
                    {item.currentUsage ?? 0}
                    {item.maxUsage != null ? ` / ${item.maxUsage}` : ''}
                  </td>
                  <td>
                    {formatDate(item.startDate)} → {formatDate(item.endDate)}
                  </td>
                  <td>
                    <span
                      className={`badge badge-light-${item.isActive ? 'success' : 'danger'}`}
                    >
                      {item.isActive
                        ? intl.formatMessage({ id: 'COMMON.YES' })
                        : intl.formatMessage({ id: 'COMMON.NO' })}
                    </span>
                  </td>
                  <td className='text-end'>
                    <button
                      type='button'
                      className='btn btn-sm btn-light-primary me-2'
                      onClick={() => {
                        setCurrentItem(item)
                        setModalOpen(true)
                      }}
                    >
                      {intl.formatMessage({ id: 'COMMON.EDIT' })}
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-light-warning me-2'
                      onClick={() => item._id && resetUsageMutation.mutate(item._id)}
                    >
                      {intl.formatMessage({ id: 'TOUR.PROMOTIONS.RESET_USAGE' })}
                    </button>
                    <button
                      type='button'
                      className='btn btn-sm btn-light-danger'
                      onClick={async () => {
                        const ok = await confirm({
                          message: intl.formatMessage({ id: 'TOUR.PROMOTIONS.DELETE_CONFIRM' }),
                        })
                        if (ok) {
                          item._id && deleteMutation.mutate(item._id as unknown as ID)
                        }
                      }}
                    >
                      {intl.formatMessage({ id: 'COMMON.DELETE' })}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={modalOpen} onHide={closeModal} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {currentItem?._id
              ? intl.formatMessage({ id: 'TOUR.PROMOTIONS.EDIT' })
              : intl.formatMessage({ id: 'TOUR.PROMOTIONS.ADD' })}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {currentItem && (
            <PromotionsForm
              ref={formRef}
              initialValues={currentItem}
              onSubmit={handleSubmit}
              onCancel={closeModal}
              intl={intl}
              tourId={tourId}
              showAllTours={showAllTours}
              isSubmitting={isSaving}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default Promotions
